import { Box, Stack, useBreakpointValue, useDisclosure, useColorModeValue, Skeleton, SimpleGrid, Button, Grid } from "@chakra-ui/react";
import React, { useEffect, useState, useContext, useMemo } from "react";
import { CollectionsFilterMenu } from "../../collections/CollectionsFilterMenu";
import {
  useConfigMap,
  useGetViewConfig,
  useProjectParams,
  useSidebarNavigation,
  useCollectionKey,
  useSkeletonProps,
  useButtonProps,
} from "hooks";
import { LandingTitle } from "screens/landing/components";
import { PausedWorkflowModalProvider } from "screens/collection/views/ProjectActions/PausedWorkflowModal";
import { CollectionsFilterContext } from "../../collections";
import { APP_CONTAINER_WIDTH } from "screens/landing/Landing";
import { AddIcon } from "@chakra-ui/icons";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import { ResearchContext } from "screens/panels/research/ResearchContext";
import { useConversationContext } from "screens/thread/ConversationContext";
import { v4 as uuid } from "uuid";
import { ProjectLandingLegacyTile } from "./ProjectLandingLegacyTile";

interface Props {
  collectionType: string;
}

export const ProjectLandingLegacy: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({ collectionType }) => {
  const { projectFilter } = useProjectParams();
  const configMap = useConfigMap();
  const { filteredCollections } = useContext(CollectionsFilterContext);
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { fallback: "md", ssr: false });
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { fallback: "md", ssr: false });
  const projectTitle = useGetViewConfig("title", projectFilter, configMap);
  const portfolioName = useCollectionKey(projectFilter, "name");
  const { isOpen: isFilterOpen, onToggle: onFilterToggle, onClose: onFilterClose } = useDisclosure();
  const { currentSidebarType } = useSidebarNavigation();
  const bgColor = useColorModeValue("#fbfbfb", "gray.800");
  const { onAddNewProjectPanelOpen, setShouldCreateNewCollection, setSelectedCollection } = useAddToCharliContext();
  const { setConversationId } = useConversationContext();
  const { setProjectType } = useContext(ResearchContext);
  const skeletonStyle = useSkeletonProps();
  const commonButtonProps = useButtonProps("sm", "primary");

  const [hasTimeoutElapsed, setTimeoutElapsed] = useState(false);
  useEffect(() => {
    if (filteredCollections.length === 0 && !hasTimeoutElapsed) {
      const timer = setTimeout(() => {
        setTimeoutElapsed(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [filteredCollections.length, hasTimeoutElapsed]);

  const containerWidth = useMemo(() => {
    return `${currentSidebarType === "hidden" && !isTablet && !isMobile ? `${APP_CONTAINER_WIDTH}px` : "100%"}`;
  }, [currentSidebarType, isMobile, isTablet]);

  useEffect(() => {
    onFilterClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter]);

  return (
    <>
      <Box height="100%" width="100%" maxWidth={containerWidth} overflow="hidden" bgColor={bgColor} pt={isMobile ? "0.5rem" : "1rem"}>
        <Box width="100%" bgColor={bgColor} zIndex={3} position="relative" height="auto">
          <LandingTitle
            mt="0"
            maxWidth={containerWidth}
            pl={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" ? "0" : "1rem"}
            pr={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" ? "0" : "1rem"}
            underline={!isMobile}
            text={
              isMobile
                ? ""
                : portfolioName
                ? portfolioName
                : `${projectTitle ? (projectTitle.toLowerCase().includes("projects") ? projectTitle : `${projectTitle} Projects`) : ""}`
            }
            titleMaxWidth="15rem"
            color="primary.default">
            <Stack justifyContent={isMobile ? "space-between" : "flex-end"} direction="row" spacing="0.5rem" pt="0" width="100%">
              <Box width="100%" maxWidth="22rem">
                <CollectionsFilterMenu onFilterToggle={onFilterToggle} isOpen={isFilterOpen} hideStateFilter hideTagFilter />
              </Box>
              <Button
                {...commonButtonProps}
                leftIcon={<AddIcon />}
                onClick={() => {
                  setShouldCreateNewCollection(true);
                  setProjectType(collectionType);
                  setConversationId(uuid());
                  setSelectedCollection(undefined);

                  onAddNewProjectPanelOpen();
                }}>
                New Project
              </Button>
            </Stack>
          </LandingTitle>
        </Box>

        <Stack bgColor={bgColor} spacing="1rem" justifyContent="space-between" mt={isMobile ? "1rem" : "0"}>
          <Stack
            spacing="1rem"
            pl={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" ? "0" : "1rem"}
            pr={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" ? "0" : "1rem"}
            pt={isMobile ? "1rem" : "1rem"}>
            <PausedWorkflowModalProvider>
              <Box mb={"5rem!important"}>
                {hasTimeoutElapsed || filteredCollections.length > 0 ? (
                  <Grid
                    templateColumns={["1fr", "repeat(2, 1fr)", "repeat(2, 1fr)", "repeat(2, 1fr)", "repeat(2, 1fr)"]}
                    gap={isMobile || isTablet ? "1rem" : "2rem"}
                    alignItems="flex-start"
                    width="100%">
                    {filteredCollections.map((collection) => (
                      <ProjectLandingLegacyTile collectionId={collection.id} />
                    ))}
                  </Grid>
                ) : (
                  <SimpleGrid spacing="1rem" columns={1}>
                    <Skeleton {...skeletonStyle} borderRadius={"md"} height="105px" width="100%" />
                    <Skeleton {...skeletonStyle} borderRadius={"md"} height="105px" width="100%" />
                    <Skeleton {...skeletonStyle} borderRadius={"md"} height="105px" width="100%" />
                    <Skeleton {...skeletonStyle} borderRadius={"md"} height="105px" width="100%" />
                  </SimpleGrid>
                )}
              </Box>
            </PausedWorkflowModalProvider>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};
